import {
  REVIEW_DECISIONS,
  type ReviewDecision,
} from "@/features/search/lib/reviews";

/**
 * Shared shape + labels for the "hide viewed" / "hide reviewed" filters.
 * Both filters pick a scope (org / project / search), who (anyone / me) and a
 * time range; `hide_reviewed` also picks which decisions to hide. Mirrors
 * `HideViewedFilterSchema` / `HideReviewedFilterSchema` in
 * `backend/app/schemas/search.py`.
 */
export type StateScope = "none" | "org" | "project" | "search";
export type StateBy = "anyone" | "me";
export type StateTimeRange = "all_time" | "custom";
export type StateTimeUnit = "days" | "weeks" | "months" | "years";

export interface StateFilterValue {
  scope?: StateScope;
  by?: StateBy;
  time_range?: StateTimeRange;
  time_value?: number | null;
  time_unit?: StateTimeUnit | null;
}

export interface HideReviewedValue extends StateFilterValue {
  decisions?: ReviewDecision[];
}

export const STATE_SCOPE_OPTIONS: { value: StateScope; label: string }[] = [
  { value: "none", label: "Don't hide" },
  { value: "search", label: "In this search" },
  { value: "project", label: "In this project" },
  { value: "org", label: "Anywhere in the org" },
];

export const STATE_BY_OPTIONS: { value: StateBy; label: string }[] = [
  { value: "anyone", label: "By anyone" },
  { value: "me", label: "By me" },
];

export const STATE_TIME_UNIT_OPTIONS: { value: StateTimeUnit; label: string }[] =
  [
    { value: "days", label: "Days" },
    { value: "weeks", label: "Weeks" },
    { value: "months", label: "Months" },
    { value: "years", label: "Years" },
  ];

export const ALL_DECISIONS: ReviewDecision[] = REVIEW_DECISIONS;

const DEFAULT_TIME_VALUE = 6;
const DEFAULT_TIME_UNIT: StateTimeUnit = "months";

export function isStateFilterActive(
  value: StateFilterValue | null | undefined,
): boolean {
  return !!value && !!value.scope && value.scope !== "none";
}

/**
 * Applies `patch` to a hide filter and normalises the result: scope "none"
 * clears the filter (`undefined`); a custom time range always carries a
 * value and unit; an all-time range carries neither.
 */
export function nextStateFilter<T extends StateFilterValue>(
  current: T | null | undefined,
  patch: Partial<T>,
  defaults: Partial<T> = {},
): T | undefined {
  const next = {
    scope: "none",
    by: "anyone",
    time_range: "all_time",
    ...defaults,
    ...(current ?? {}),
    ...patch,
  } as T;
  if (next.scope === "none") return undefined;
  if (next.time_range === "custom") {
    next.time_value = next.time_value || DEFAULT_TIME_VALUE;
    next.time_unit = next.time_unit ?? DEFAULT_TIME_UNIT;
  } else {
    next.time_value = undefined;
    next.time_unit = undefined;
  }
  return next;
}
